import { memo } from 'react'
import type { Greeks, OrderSideValue } from '@apna/shared'
import { cn } from '@/lib/utils'
import { useTickCell } from '@/feed/useTick'
import { GreeksCell } from './GreeksCell'
import type { ChainMode } from './chainMode'
import { ChainLimitButton } from './ChainLimitButton'
import { heldLabel, type HeldContract, totalTitle } from './manualHeld'
import { useManualHeld, useTotalHeld } from './heldStore'
import type { ChainLimitSelection } from './chainLimitOrder'

/**
 * One strike of the chain — ROADMAP 4.10.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY LIVE NUMBER IS ITS OWN SUBSCRIPTION.
 *
 * §4.7: 460 cells reconciling at 10-15 Hz is 4,600 renders a second. Each
 * price and each OI below reads ITS OWN slot through `useSyncExternalStore`,
 * and `memo` keeps a table-wide re-render from walking the rest.
 *
 * IV ARRIVES AS A PROP, NOT A SUBSCRIPTION — AND THAT IS DELIBERATE.
 *
 * Every other number here reads its own slot so one tick repaints one cell.
 * IV does the opposite: it is solved centrally on a fixed cadence (6.6,
 * `useChainGreeks`) and handed down. Solving per cell would re-bisect eighty
 * options at feed rate, and worse, would give the row above and the row
 * below different forwards and different times to expiry — a skew that is an
 * artefact of render timing rather than a fact about the market.
 *
 * OI still needs message code 1510, which the demand path does not subscribe
 * today. It and an unsolved IV both render as "—" rather than a zero: a zero
 * is a value, and the project's rule is that a chain showing invented
 * numbers is worse than one showing none.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface ChainRowProps {
  /**
   * Which columns this row draws — ROADMAP 4.20.
   *
   *   MARKET    OI, ITM%, delta, IV, LTP — the full ladder
   *   GREEKS    the four risks in place of OI and ITM%
   *   COMBINED  delta, IV, LTP only, plus a COMBINED section: the straddle
   *             price for this strike and one pair of buttons for both legs
   */
  mode?: ChainMode
  strike: number
  ceToken: number | null
  peToken: number | null
  /** Live spot, for the ITM shading. 0 while unknown. */
  spot: number
  isAtm: boolean
  /** Largest OI on each side, for the bar scale. 0 disables the bars. */
  maxCeOi: number
  maxPeOi: number
  /**
   * Draw open interest under the price.
   *
   * Decided by the CALLER, not from `mode` here, because it depends on
   * something a row cannot see: COMBINED shows it only when the chain has
   * the whole window. Passing the answer keeps that rule in one place.
   */
  showOi?: boolean
  onTrade: (token: number, strike: number, optionSide: 'CE' | 'PE', side: OrderSideValue) => void
  onLimit?: (selection: ChainLimitSelection) => void
  /**
   * Both sides of this strike, in ONE order — ROADMAP 4.18.
   *
   * Drawn in BOTH modes. It lives in the strike column, which is the one
   * part of the row Greeks mode does not rebuild — so the control stays
   * exactly where the hand left it when the mode is toggled.
   */
  onCombo?: (strike: number, ceToken: number, peToken: number, side: OrderSideValue) => void
  /**
   * The account the NEXT order goes to — ROADMAP 9.11.
   *
   * Only used to look up what this strike already holds there, which is the
   * only account a click on this chain could reduce. Null means no account
   * is chosen, and then nothing is marked.
   */
  connectionId?: string | null
  /** Orders cannot be placed without an account, so the buttons say so. */
  tradeDisabled: boolean
  /**
   * What B and S DO on this ladder — ROADMAP 9.66.
   *
   * 'FIRE' is the Terminal: the click sends an order, immediately, with no
   * confirmation (4.15). 'ADD' is the basket builder, where nothing on the
   * ladder can reach the order path at all.
   *
   * It changes the LABEL and the accessible name, and nothing else. The
   * buttons stay in the same place and keep the same colours, because that
   * is the muscle memory — and the two meanings are kept apart by being on
   * two different screens rather than by this flag. A reader of this row
   * should never have to ask which one they are on; see the builder's own
   * header for why that is a separate surface.
   */
  action?: 'FIRE' | 'ADD'
  /**
   * Implied volatility as a FRACTION (0.14 = 14%), or null when this option
   * could not be solved. `ceIvWhy` / `peIvWhy` say why, for the tooltip.
   */
  ceIv?: number | null
  peIv?: number | null
  ceIvWhy?: string
  peIvWhy?: string
  /** N(d1). Solved centrally with the IV — see the header. */
  ceDelta?: number | null
  peDelta?: number | null
  ceGreeks?: Greeks | null
  peGreeks?: Greeks | null
  /** N(d2) as a FRACTION. The chance of finishing in the money. */
  ceItmPct?: number | null
  peItmPct?: number | null
}

/** IV as a trader reads it: vol points, one decimal. */
const vol = (iv: number | null | undefined): string =>
  iv === null || iv === undefined ? '\u2014' : `${(iv * 100).toFixed(1)}`

const price = (v: number): string =>
  v > 0 ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'

/**
 * Delta, two decimals and signed.
 *
 * Signed even for a call, where it is always positive, because the column
 * holds both sides and a put's minus sign must not read as the odd one out.
 */
const delta = (d: number | null | undefined): string => {
  if (d === null || d === undefined) return '\u2014'
  const r = d.toFixed(2)
  // `(-0.004).toFixed(2)` is "-0.00", which reads as a bug rather than as a
  // delta too small to show. The sign carries no information once the digits
  // are gone.
  return r === '-0.00' ? '0.00' : r
}

/**
 * The chance of finishing in the money, as whole percent.
 *
 * NO DECIMAL. It is a probability from a model fitted to one mid price; the
 * second digit would move with every tick and imply a precision the number
 * does not have.
 */
const itmPct = (p: number | null | undefined): string =>
  p === null || p === undefined ? '\u2014' : `${Math.round(p * 100)}%`

/** OI in lakhs, which is how the number is read on a desk. */
const lakhs = (v: number): string => (v > 0 ? `${(v / 100_000).toFixed(1)}L` : '—')

/**
 * A signed lot count on a strike this account already holds — ROADMAP 9.11.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IN THE PREMIUM CELL, AND THAT PLACEMENT IS THE WHOLE DESIGN DECISION
 *
 * The obvious home is beside B and S. There is no room: that column is a
 * fixed 110px holding three 30px buttons and two 4px gaps — 98px used, 12px
 * spare — so a readable chip there means re-cutting eight
 * `grid-template-columns` declarations and `--tr-chain-min` with it. The
 * chain's column widths have been re-derived five times already; a badge is
 * not worth a sixth.
 *
 * The premium column is `minmax(76px, 1fr)`. It is ELASTIC, it is where the
 * eye already goes for that side, and a chip in it competes with a price of
 * about 48px inside a 76px floor. `shrink-0` on the chip and the shed rule
 * in the stylesheet handle the floor.
 *
 * NEVER PROMISES WHAT A CLICK WILL DO. The browser does not know whether
 * netting is switched on for this trader — there is no endpoint that would
 * tell it — so this states a POSITION and nothing more. A trader who turned
 * netting on knows a sell here reduces; one who did not reads a true fact
 * about what they hold either way.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * What this contract holds — ROADMAP 9.27.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE NUMBER IS EVERYTHING; THE STYLE IS WHAT A CLICK WOULD DO
 *
 * The chip used to show only what the manual block of the selected account
 * held, because it was built to answer "will this click reduce?" — and that
 * left a strike held 20 lots deep inside an Iron Condor looking untouched.
 * A trader reading the chain for exposure got a blank.
 *
 * So the number is now the TOTAL, across every broker account, every
 * strategy and every named group. The netting promise is kept by the chip's
 * appearance rather than by hiding the figure: solid where some of it is
 * loose on this account and a click genuinely reduces, outlined where the
 * exposure sits inside a strategy and a click would open something new.
 *
 * Hiding the total to protect the promise was the old trade and it was the
 * wrong way round — a trader can be told what a click does, but cannot
 * guess at a position the screen does not show.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function HeldChip({
  total,
  nettable,
  token,
}: {
  total: HeldContract | null
  nettable: HeldContract | null
  token: number | null
}) {
  if (total === null || token === null) return null
  const reduces = nettable !== null && nettable.units !== 0
  return (
    <span
      className={cn(
        'tr-held',
        total.units > 0 ? 'long' : 'short',
        // Exposure we hold but cannot net here reads quieter, and says so
        // in the tooltip. It is still a position, so it is still drawn.
        !reduces && 'tr-held-locked',
      )}
      title={totalTitle(total, nettable)}
      data-testid={`held-${token}`}
      data-nets={reduces ? 'true' : 'false'}
    >
      {heldLabel(total)}
    </span>
  )
}

function ChainRowInner({
  mode = 'MARKET',
  strike,
  ceToken,
  peToken,
  spot,
  isAtm,
  maxCeOi,
  maxPeOi,
  showOi = false,
  onTrade,
  onCombo,
  connectionId = null,
  onLimit,
  tradeDisabled,
  action = 'FIRE',
  ceIv,
  peIv,
  ceIvWhy,
  peIvWhy,
  ceDelta,
  peDelta,
  ceGreeks,
  peGreeks,
  ceItmPct,
  peItmPct,
}: ChainRowProps) {
  const { value: ceLtp } = useTickCell(ceToken ?? 0, 'ltp')
  const { value: peLtp } = useTickCell(peToken ?? 0, 'ltp')
  const { value: ceOi } = useTickCell(ceToken ?? 0, 'oi')
  const { value: peOi } = useTickCell(peToken ?? 0, 'oi')

  /*
   * 9.11. What the MANUAL block of the selected account holds on each side.
   *
   * Subscribed per row, which is the pattern this chain already uses for
   * ticks — and far cheaper than those, because it changes on a fill rather
   * than at 15 Hz. Strategy and combined-premium legs are excluded by
   * `manualHeldFrom`, so a marked side is one a click would genuinely
   * reduce.
   */
  const ceHeld = useManualHeld(connectionId, ceToken)
  const peHeld = useManualHeld(connectionId, peToken)
  /*
   * 9.27. Everything held on this contract anywhere — strategies, named
   * groups, other accounts. The chip shows this and uses the pair above
   * only to decide whether it is drawn as a reduce.
   */
  const ceTotal = useTotalHeld(ceToken)
  const peTotal = useTotalHeld(peToken)

  // A call is in the money below the spot, a put above it. Needs a real
  // spot: shading the whole chain because the spot is 0 would be worse
  // than shading none of it.
  const ceItm = spot > 0 && strike < spot
  const peItm = spot > 0 && strike > spot

  return (
    <div className={cn('tr-row tr-grid', isAtm && 'atm')} data-testid={`chain-row-${strike}`}>
      <div className={cn('tr-side ce', ceItm && 'itm', ceTotal && 'held')}>
        {mode === 'GREEKS' ? <GreeksCell greeks={ceGreeks} side="ce" why={ceIvWhy} /> : mode === 'COMBINED' ? null : (
        <div className="tr-cell tr-r tr-itm num" title={ceIvWhy}>
          {itmPct(ceItmPct)}
        </div>
        )}
        <div className="tr-cell tr-r tr-dlt num" title={ceIvWhy}>
          {delta(ceDelta)}
        </div>
        <div className="tr-cell tr-r tr-iv num" title={ceIvWhy}>
          {vol(ceIv)}
        </div>
        <div className="tr-cell tr-r tr-ltp num">
          <span className="tr-ltpline">
            <HeldChip total={ceTotal} nettable={ceHeld} token={ceToken} />
            {price(ceLtp)}
          </span>
        </div>
        <div className="tr-cell tr-c">
          <BuySell
            token={ceToken}
            strike={strike}
            optionSide="CE"
            onTrade={onTrade}
            onLimit={onLimit}
            disabled={tradeDisabled}
            action={action}
          />
        </div>
        {showOi && <OiUnderline value={ceOi} max={maxCeOi} />}
      </div>

      <div className="tr-cell tr-strike num">
        {strike.toLocaleString('en-IN')}
        {isAtm && <span className="tr-atmtag">ATM</span>}
      </div>

      <div className={cn('tr-side pe', peItm && 'itm', peTotal && 'held')}>
        <div className="tr-cell tr-c">
          <BuySell
            token={peToken}
            strike={strike}
            optionSide="PE"
            onTrade={onTrade}
            onLimit={onLimit}
            disabled={tradeDisabled}
            action={action}
          />
        </div>
        <div className="tr-cell tr-l tr-ltp num">
          <span className="tr-ltpline">
            {price(peLtp)}
            <HeldChip total={peTotal} nettable={peHeld} token={peToken} />
          </span>
        </div>
        <div className="tr-cell tr-l tr-iv num" title={peIvWhy}>
          {vol(peIv)}
        </div>
        <div className="tr-cell tr-l tr-dlt num" title={peIvWhy}>
          {delta(peDelta)}
        </div>
        {mode === 'GREEKS' ? <GreeksCell greeks={peGreeks} side="pe" why={peIvWhy} /> : mode === 'COMBINED' ? null : (
        <div className="tr-cell tr-l tr-itm num" title={peIvWhy}>
          {itmPct(peItmPct)}
        </div>
        )}
        {showOi && <OiUnderline value={peOi} max={maxPeOi} />}
      </div>

      {mode === 'COMBINED' && onCombo && (
        <div className="tr-side cmb">
          <Combined
            strike={strike}
            ceToken={ceToken}
            peToken={peToken}
            ceLtp={ceLtp}
            peLtp={peLtp}
            onCombo={onCombo}
            onLimit={onLimit}
            disabled={tradeDisabled}
          />
        </div>
      )}

    </div>
  )
}

/**
 * The COMBINED PREMIUM section — ROADMAP 4.18.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT A STRADDLE AT THIS STRIKE COSTS, AND WHICH SIDE IS PAYING FOR IT
 *
 * The total is the headline. The two percentages are the shape: a 90/10
 * split says the call is carrying the whole premium and the strike is deep
 * in the money, and 50/50 says you are at the money. Reading that down the
 * page is how a discretionary trader finds the money without a spot line.
 *
 * THE ORDER IS FIXED: Total, CE %, PE %, then the actions. The numbers are
 * what is read and the buttons are what is pressed, so the reading runs
 * left to right and the pressing ends at the edge where every other
 * section's buttons already are.
 *
 * NO PRICE, NO BUTTONS. An option that has not ticked is a ZERO in the tick
 * store, and `0 + 88.35` renders as a straddle costing 88.35 — a number
 * that looks right and is not. An unpriced side shows a dash and refuses
 * the click, because in ₹ mode the order would be sized against that zero.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function Combined({
  strike,
  ceToken,
  peToken,
  ceLtp,
  peLtp,
  onCombo,
  onLimit,
  disabled,
}: {
  strike: number
  ceToken: number | null
  peToken: number | null
  ceLtp: number
  peLtp: number
  onCombo: NonNullable<ChainRowProps['onCombo']>
  onLimit?: ChainRowProps['onLimit']
  disabled: boolean
}) {
  const listed = ceToken !== null && peToken !== null
  const priced = listed && ceLtp > 0 && peLtp > 0
  const total = ceLtp + peLtp
  const label = `${strike.toLocaleString('en-IN')} call and put together`
  return (
    <>
      <div className="tr-cell tr-r tr-cmbp num">
        {priced ? (
          <span data-testid={`combo-premium-${strike}`} title={`${price(ceLtp)} call + ${price(peLtp)} put`}>
            {price(total)}
          </span>
        ) : (
          <span data-testid={listed ? `combo-premium-${strike}` : undefined} className="tr-faintnum">
            {'\u2014'}
          </span>
        )}
      </div>
      <div className="tr-cell tr-r tr-cmbce num">{priced ? share(ceLtp, total) : '\u2014'}</div>
      <div className="tr-cell tr-r tr-cmbpe num">{priced ? share(peLtp, total) : '\u2014'}</div>
      <div className="tr-cell tr-c">
        {listed && (
          <div className="tr-bs">
            <button
              type="button"
              className="tr-bsb b"
              disabled={disabled || !priced}
              aria-label={`Buy ${label}`}
              data-testid={`combo-buy-${strike}`}
              title={priced ? `Buy BOTH legs as one basket — ${price(total)} per unit` : 'One side is unpriced; the combined premium is unknown'}
              onClick={() => onCombo(strike, ceToken, peToken, 'BUY')}
            >
              B
            </button>
            <button
              type="button"
              className="tr-bsb s"
              disabled={disabled || !priced}
              aria-label={`Sell ${label}`}
              data-testid={`combo-sell-${strike}`}
              title={priced ? `Sell BOTH legs as one basket — ${price(total)} per unit` : 'One side is unpriced; the combined premium is unknown'}
              onClick={() => onCombo(strike, ceToken, peToken, 'SELL')}
            >
              S
            </button>
            <ChainLimitButton onClick={onLimit ? () => onLimit({ strike, legs: [{ token: ceToken, optionType: 'CE' }, { token: peToken, optionType: 'PE' }] }) : undefined} disabled={disabled} label={`${strike} call and put`} testId={`combo-limit-${strike}`} />
          </div>
        )}
      </div>
    </>
  )
}

/**
 * One side's share of the straddle, as whole percent.
 *
 * No decimal, for the reason `itmPct` has none: the second digit would move
 * on every tick and imply a precision two last-traded prices do not have.
 */
const share = (part: number, total: number): string =>
  total > 0 ? `${Math.round((part / total) * 100)}%` : '\u2014'

/**
 * Open interest, UNDER the price rather than beside it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT GAVE UP A COLUMN AND GOT A WIDER LADDER FOR IT
 *
 * OI used to own 84-100px at the outer edge of each side, which on a split
 * workspace is most of the reason the chain scrolled sideways. As the side's
 * own underline it costs no width at all, and it puts the two numbers a
 * trader reads together — where the crowd is, and what it costs — in one
 * place instead of at opposite ends of the row.
 *
 * A CHILD OF THE SIDE, NOT OF THE PRICE CELL, and absolutely positioned
 * against it: inside the price cell it was as wide as one of five tracks,
 * which is a stub rather than a scale. Which half it is drawn on is not a
 * prop — `.tr-side.pe` already says so, and a component that took a `side`
 * it did not otherwise use was one more thing that could disagree with the
 * element it sits in.
 *
 * THE BAR GROWS FROM THE STRIKE OUTWARD on both sides, so the two halves
 * mirror and the eye reads the ladder outward from the middle the way every
 * other column on this row already does.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function OiUnderline({ value, max }: { value: number; max: number }) {
  const pct = max > 0 && value > 0 ? Math.min(100, (value / max) * 100) : 0
  // The highest OI on the side is the one worth finding at a glance.
  const isMax = max > 0 && value > 0 && value >= max
  return (
    <span className="tr-oiu" aria-hidden={value <= 0}>
      <span className="tr-track">
        <span className={cn('tr-fill', isMax && 'max')} style={{ width: `${pct}%` }} />
      </span>
      <span className="n num">{lakhs(value)}</span>
    </span>
  )
}

function BuySell({
  token,
  strike,
  optionSide,
  onTrade,
  onLimit,
  disabled,
  action = 'FIRE',
}: {
  token: number | null
  strike: number
  optionSide: 'CE' | 'PE'
  onTrade: ChainRowProps['onTrade']
  onLimit?: ChainRowProps['onLimit']
  disabled: boolean
  action?: 'FIRE' | 'ADD'
}) {
  if (token === null) {
    // No contract on this side of this strike. An empty cell keeps the
    // column widths steady rather than collapsing the row.
    return <div className="tr-bs" />
  }
  const word = optionSide === 'CE' ? 'call' : 'put'
  const add = action === 'ADD'
  return (
    <div className="tr-bs">
      <button
        type="button"
        className="tr-bsb b"
        disabled={disabled}
        aria-label={add ? `Add a bought ${strike} ${word}` : `Buy ${strike} ${word}`}
        data-testid={`buy-${token}`}
        onClick={() => onTrade(token, strike, optionSide, 'BUY')}
      >
        {add ? '+B' : 'B'}
      </button>
      <button
        type="button"
        className="tr-bsb s"
        disabled={disabled}
        aria-label={add ? `Add a sold ${strike} ${word}` : `Sell ${strike} ${word}`}
        data-testid={`sell-${token}`}
        onClick={() => onTrade(token, strike, optionSide, 'SELL')}
      >
        {add ? '+S' : 'S'}
      </button>
      <ChainLimitButton onClick={onLimit ? () => onLimit({ strike, legs: [{ token, optionType: optionSide }] }) : undefined} disabled={disabled} label={`${strike} ${word}`} testId={`limit-${token}`} />
    </div>
  )
}

export const ChainRow = memo(ChainRowInner)
